import React, { type MouseEvent } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { CenteredCardContent, StyledLinearProgressBar } from './styles'
import { useSnackbar } from 'notistack'
import ROUTES from '../../../../consts/paths'
import { formatDate } from '../../../../../src/utils/utils'
import { getPercent } from '../../../../helpers'
import GenericButton from '../../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'
import { grey, red, shade, yellow } from '../../../../colors'
import { Chip, Divider, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigateToUrl } from '../../../../hooks/useNavigateToUrl'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import { SlotStatus } from '../../../../models/DistributionPlan'
import { type InstructorDistribution } from '../../../../models/Distribution'
import CustomTooltip from '../../../../components/CustomTooltip'
export interface InstructorSurveyListCardProps {
  distributionId: string
  distributionSeriesId: string
  surveyNumber: number
  totalSurveys: number
  courseName: string
  sectionAbbr: string
  endDate: string
  startDate: string
  releaseDate: string
  surveyURL: string
  reportURL: string
  studentCount: number
  completionCount: number
  slotNodeStatus: string
  sectionId: string
  slotId: string
  slotTitle: string
  onClick?: () => Promise<void>
  distribution: InstructorDistribution
  isSlotAnonymous?: boolean
  isInstructorAddedQuestionsAllowed?: boolean
}

enum PrimaryActionButtonText {
  CopyStudentLink = 'Copy Student Link',
  ViewResults = 'View Results'
}

const InstructorSurveyListCard: React.FC<InstructorSurveyListCardProps> = ({
  distributionId,
  distributionSeriesId,
  surveyNumber,
  totalSurveys,
  courseName,
  sectionAbbr,
  endDate,
  startDate,
  releaseDate,
  completionCount,
  studentCount,
  surveyURL,
  slotId,
  reportURL,
  sectionId,
  slotNodeStatus,
  slotTitle,
  isSlotAnonymous,
  isInstructorAddedQuestionsAllowed,
  onClick = () => { },
  distribution
}): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const navigateToURL = useNavigateToUrl()
  const currDateObj = new Date()
  const releaseDateObj = new Date(releaseDate)
  const endDateStr = formatDate(endDate)
  const startDateStr = formatDate(startDate)
  const releaseDateStr = formatDate(releaseDate)
  const percentValue = isSlotAnonymous ? 0 : getPercent(completionCount, studentCount)
  const progressBarValue = isSlotAnonymous ? 100 : percentValue
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { search } = useLocation()
  const urlParams = new URLSearchParams(search)
  const termId = urlParams.get('termId') ?? ''
  // The survey for the course section is considered "Released" when its SlotNode status is "Closed"
  // and the current date is on or after the release date.
  const isSlotNodeReleased = slotNodeStatus === SlotStatus.Closed && currDateObj >= releaseDateObj

  // Display 'Copy Student Link' when the Survey is in Draft (disabled), Published (enabled), or Live (enabled) states.
  // Display 'View Results' when the Survey is in Closed (disabled) or Released (enabled) states.

  const primaryActionButtonText = (slotNodeStatus !== SlotStatus.Closed)
    ? PrimaryActionButtonText.CopyStudentLink
    : PrimaryActionButtonText.ViewResults
  const primaryActionButtonType = (slotNodeStatus !== SlotStatus.Closed) ? ButtonType.Secondary : ButtonType.Primary

  const isCopyLink = primaryActionButtonText === PrimaryActionButtonText.CopyStudentLink

  const isPrimaryActionButtonDisabled =
    (slotNodeStatus === SlotStatus.Draft) ||
    (slotNodeStatus === SlotStatus.Closed && currDateObj < releaseDateObj) ||
    // NEW rules that apply only when the button *is* "Copy Student Link"
    (isCopyLink && (slotNodeStatus !== SlotStatus.Live || !distributionId))

  const isPreviewSurveyButtonEnabled = slotNodeStatus !== SlotStatus.Closed
  // Enable the 'Display QR Code' button when the Survey is in Published or Live states.
  const isQRCodeButtonEnabled = slotNodeStatus === SlotStatus.Live

  const handlePrimaryActionButtonOnClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (isSlotNodeReleased && reportURL.length > 0 && reportURL.trim() !== '') {
      navigateToURL(event, `${ROUTES.REPORTS}/${reportURL}?termId=${searchParams.get('termId')}&seriesId=${distributionSeriesId}`, true)
    } else if (slotNodeStatus === SlotStatus.Published || slotNodeStatus === SlotStatus.Live) {
      const baseURL = window.location.origin
      const updatedSurveyURL = isSlotAnonymous ? `${baseURL}/public/surveys/${distributionId}?isPublic=true` : `${baseURL}/surveys/${distributionId}`
      void navigator.clipboard.writeText(updatedSurveyURL).then(() => {
        onClick()
        enqueueSnackbar('Survey link successfully copied!', { variant: 'success' })
      }).catch(error => {
        enqueueSnackbar('Failed to copy to clipboard:', error)
      })
    }
  }
  const handleQRCodeButtonOnClick = (event: MouseEvent<HTMLButtonElement>): void => {
    const baseURL = window.location.origin
    const qrCodeURL = `${baseURL}${ROUTES.QR_CODE}/?sectionId=${encodeURIComponent(sectionAbbr)}&sectionTitle=${encodeURIComponent(courseName)}&slotNodeStatus=${encodeURIComponent(slotNodeStatus)}&distributionId=${encodeURIComponent(distributionId)}&isSlotAnonymous=${isSlotAnonymous ? 'true' : 'false'}`
    navigateToURL(event, qrCodeURL, true)
  }

  const handlePreviewSurveyOnClick = (event: MouseEvent<HTMLButtonElement>): void => {
    const baseURL = window.location.origin
    const surveyPreviewURL = `${baseURL}${ROUTES.SURVEY_PREVIEW}/${slotId}/${sectionId}`
    navigateToURL(event, surveyPreviewURL, true)
  }

  const handleAddQuestion = (event: MouseEvent<HTMLButtonElement>): void => {
    const questionCount = distribution?.instructorAddedQuestions?.questionCount ?? 0
    const params = new URLSearchParams({
      slotId: distribution?.slotId ?? '',
      divisionId: distribution?.sectionId ?? '',
      questionCount: questionCount.toString(),
      categoryName: distribution?.sectionDetails?.courseName ?? '',
      type: 'Instructor-added Questions',
      isInstructorAddedQuestionsAllowed: distribution?.instructorAddedQuestions?.isCustomQuestionAllowed ? 'true' : 'false',
      addedQuestionCount: distribution?.questionCount?.toString() ?? '',
      termId: termId ?? ''
    })
    navigate(`${ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS}?${params.toString()}`)
  }

  const isDisabledAddQuestionButton = !isInstructorAddedQuestionsAllowed || (slotNodeStatus !== SlotStatus.Published)
  const addQuestionButtonTooltipTitle = (): string => {
    if (isDisabledAddQuestionButton) {
      return 'The ability to add additional questions is disabled for this Survey'
    }

    return ''
  }

  const renderSurveyDateDetails = (
    startDateStr: string,
    endDateStr: string
  ): JSX.Element => {
    try {
      switch (slotNodeStatus) {
        case SlotStatus.Draft:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Starts: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Published:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Starts: <b>{startDateStr}</b>
            </Typography>
          )
        case SlotStatus.Live:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Survey Ends: <b>{endDateStr}</b>
            </Typography>
          )
        case SlotStatus.Closed:
          return (
            <Typography fontWeight='500' fontSize='14px' color={grey[900]} variant="caption">
              Results Released: <b>{releaseDateStr}</b>
            </Typography>
          )
        default:
          return <></>
      }
    } catch (error) {
      return <></>
    }
  }

  return (
    <Card tabIndex={0} aria-label={`survey ${surveyNumber} of ${totalSurveys} ${courseName}`} style={{ marginBottom: '2rem' }}>
      {
        slotNodeStatus === null
          ? (
            // Render a circular loader when slotNodeStatus is null
            <CenteredCardContent>
              <CircularProgress />
            </CenteredCardContent>
            )
          : (
            // Render the card content when surveyState is not null
            <>
              <CardContent >
                <Box display="flex" justifyContent="space-between" >
                  <Box>
                    <Typography variant="p4">
                      {slotTitle}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={4}>
                      <Typography mt={2} variant="h5" gutterBottom>
                        {sectionAbbr}
                      </Typography>
                      {isSlotAnonymous && (
                        <Chip
                          label="Unverified Submissions"
                          variant="filled"
                          size="small"
                          sx={{
                            borderRadius: '6px',
                            border: `1px solid ${yellow[100]}`,
                            fontWeight: 500,
                            fontSize: '12px',
                            padding: '0px 8px',
                            color: red[800],
                            backgroundColor: shade[1000]
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="p4">
                      {courseName}
                    </Typography>
                  </Box>
                  <Divider />
                  {
                    !isMobile &&
                    <CustomTooltip
                      title={
                        isPrimaryActionButtonDisabled && isCopyLink
                          ? 'This button will be enabled when the survey is in the Live state.'
                          : ''
                      }
                    >

                      <GenericButton
                        buttonType={primaryActionButtonType}
                        label={primaryActionButtonText}
                        fontWeight='600'
                        buttonSize={ButtonSize.Medium}
                        onClick={handlePrimaryActionButtonOnClick}
                        disabled={isPrimaryActionButtonDisabled}
                      />
                    </CustomTooltip>
                  }

                </Box>
              </CardContent>
              <CardContent aria-label={`Survey closed${percentValue}% completed ${completionCount}/${studentCount} Responses`} tabIndex={0}>
                <Box display='flex' justifyContent='space-between'>
                  {renderSurveyDateDetails(startDateStr, endDateStr)}
                </Box>
                <Box padding={theme.spacing(2)}>
                  <StyledLinearProgressBar value={progressBarValue} isNotClosed={slotNodeStatus !== SlotStatus.Closed} isAnonymous={isSlotAnonymous} />
                </Box>
                <Box display='flex' justifyContent='space-between'>
                  <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                    {percentValue}% Complete
                  </Typography>
                  <Typography color={grey[950]} fontWeight='500' component="h4" variant="caption">
                    {isSlotAnonymous ? `${completionCount} Responses` : `${completionCount}/${studentCount} Responses`}
                  </Typography>
                </Box>
                <Divider sx={{ padding: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 6 }}>
                  <GenericButton
                    buttonType={ButtonType.Secondary}
                    label="Preview Survey"
                    fontWeight='600'
                    startIcon={<RemoveRedEyeOutlinedIcon />}
                    buttonSize={ButtonSize.Medium}
                    onClick={handlePreviewSurveyOnClick}
                    disabled={!isPreviewSurveyButtonEnabled}
                  />
                  <GenericButton
                    buttonType={ButtonType.Secondary}
                    label="Display QR Code"
                    fontWeight='600'
                    startIcon={<QrCode2OutlinedIcon />}
                    buttonSize={ButtonSize.Medium}
                    onClick={handleQRCodeButtonOnClick}
                    disabled={!isQRCodeButtonEnabled}
                  />

                  <CustomTooltip title={addQuestionButtonTooltipTitle()}>
                    <GenericButton
                      buttonType={ButtonType.Secondary}
                      label={`Add Questions ${typeof distribution?.questionCount === 'number' && distribution.questionCount > 0 ? ` (${distribution.questionCount})` : ''}`}
                      fontWeight='600'
                      buttonSize={ButtonSize.Medium}
                      onClick={handleAddQuestion}
                      disabled={isDisabledAddQuestionButton}
                    />
                  </CustomTooltip>
                </Box>
                {
                  isMobile &&
                  <CustomTooltip
                    title={
                      isPrimaryActionButtonDisabled && isCopyLink
                        ? 'This button will be enabled when the survey is in the Live state.'
                        : ''
                    }
                  >
                    <span>
                      <GenericButton
                        fullWidth
                        buttonType={primaryActionButtonType}
                        label={primaryActionButtonText}
                        fontWeight='600'
                        buttonSize={ButtonSize.Medium}
                        style={{ marginTop: theme.spacing(5) }}
                        onClick={handlePrimaryActionButtonOnClick}
                        disabled={isPrimaryActionButtonDisabled}
                      />
                    </span>
                  </CustomTooltip>
                }
              </CardContent>
            </>
            )}
    </Card>
  )
}
export default InstructorSurveyListCard
